import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface AtsVenta {
  tipo_comprobante: '01' | '04';
  numero: string;
  clave_acceso: string | null;
  fecha_emision: string;
  estado: 'NORMAL' | 'ANULADO';
  cliente_id: string;
  cliente_razon: string;
  total: string;
}
interface AtsCompra {
  numero: string;
  fecha_emision: string;
  estado: 'NORMAL' | 'ANULADO';
  proveedor_id: string;
  proveedor_razon: string;
  total: string;
}
interface AtsReport {
  header: {
    ruc: string;
    razon_social: string;
    anio: string;
    mes: string;
    total_ventas: string;
    total_compras: string;
    total_retenciones: string;
  };
  ventas: AtsVenta[];
  compras: AtsCompra[];
}

const fmt = (cents: string): string => {
  const n = BigInt(cents);
  const sign = n < 0n ? '-' : '';
  const abs = n < 0n ? -n : n;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
};

export function AtsPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [establishmentCode, setEstablishmentCode] = useState<string>('');

  const { data: epList } = useQuery({
    queryKey: ['emission-points', activeOrgId],
    queryFn: () => api<Array<{ establishment_code: string }>>('/bookkeeping/emission-points'),
    enabled: !!activeOrgId,
  });
  const uniqueEstabs = Array.from(
    new Set((epList ?? []).map((ep) => ep.establishment_code)),
  ).sort();

  const { data, isLoading, error } = useQuery({
    queryKey: ['ats', activeOrgId, year, month, establishmentCode],
    queryFn: () =>
      api<AtsReport>(
        `/bookkeeping/reports/ats?year=${year}&month=${month}${establishmentCode ? `&establishment_code=${establishmentCode}` : ''}`,
      ),
    enabled: !!activeOrgId,
  });

  const downloadXml = () => {
    const url = `${import.meta.env.VITE_API_URL ?? '/api'}/bookkeeping/reports/ats?year=${year}&month=${month}&format=xml`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('reports.ats.title')}</h1>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => setYear(Number.parseInt(e.target.value, 10))}
            className="w-24 rounded border px-2 py-1 text-sm"
            aria-label={t('reports.ats.year')}
          />
          <select
            value={month}
            onChange={(e) => setMonth(Number.parseInt(e.target.value, 10))}
            className="rounded border px-2 py-1 text-sm"
            aria-label={t('reports.ats.month')}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
          {uniqueEstabs.length >= 2 && (
            <select
              value={establishmentCode}
              onChange={(e) => setEstablishmentCode(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
              aria-label={t('reports.filter_establishment')}
            >
              <option value="">{t('reports.filter_establishment_all')}</option>
              {uniqueEstabs.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            disabled={!data}
            onClick={downloadXml}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
          >
            {t('reports.ats.download_xml')}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(error as { message?: string }).message ?? 'Error'}
        </p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card label={t('reports.ats.total_ventas')} value={fmt(data.header.total_ventas)} />
            <Card label={t('reports.ats.total_compras')} value={fmt(data.header.total_compras)} />
            <Card
              label={t('reports.ats.total_retenciones')}
              value={fmt(data.header.total_retenciones)}
            />
            <Card label={t('reports.ats.ruc')} value={data.header.ruc} mono />
          </div>

          <Section title={`${t('reports.ats.ventas')} (${data.ventas.length})`}>
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('reports.ats.tipo')}</th>
                  <th className="px-3 py-2">{t('reports.ats.numero')}</th>
                  <th className="px-3 py-2">{t('reports.ats.clave_acceso')}</th>
                  <th className="px-3 py-2">{t('reports.ats.cliente')}</th>
                  <th className="px-3 py-2">{t('reports.ats.fecha')}</th>
                  <th className="px-3 py-2 text-right">{t('reports.ats.total')}</th>
                  <th className="px-3 py-2">{t('reports.ats.estado')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ventas.map((v) => (
                  <tr key={v.numero} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono text-xs">{v.tipo_comprobante}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{v.numero}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-500">
                      {v.clave_acceso ? `${v.clave_acceso.slice(0, 12)}…` : '—'}
                    </td>
                    <td className="px-3 py-1.5">{v.cliente_razon}</td>
                    <td className="px-3 py-1.5">{v.fecha_emision}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(v.total)}</td>
                    <td className="px-3 py-1.5">{v.estado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title={`${t('reports.ats.compras')} (${data.compras.length})`}>
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('reports.ats.numero')}</th>
                  <th className="px-3 py-2">{t('reports.ats.proveedor')}</th>
                  <th className="px-3 py-2">{t('reports.ats.fecha')}</th>
                  <th className="px-3 py-2 text-right">{t('reports.ats.total')}</th>
                  <th className="px-3 py-2">{t('reports.ats.estado')}</th>
                </tr>
              </thead>
              <tbody>
                {data.compras.map((c) => (
                  <tr key={c.numero} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono text-xs">{c.numero}</td>
                    <td className="px-3 py-1.5">{c.proveedor_razon}</td>
                    <td className="px-3 py-1.5">{c.fecha_emision}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(c.total)}</td>
                    <td className="px-3 py-1.5">{c.estado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

function Card({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded border bg-white p-3">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded border bg-white">
      <h2 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">{title}</h2>
      {children}
    </div>
  );
}
